import { useState } from "react";
import { Rss, Copy, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { show, feedUrl } from "@/lib/podcast";
import { siteConfig } from "@/config/siteConfig";

export default function Follow() {
  const [copied, setCopied] = useState(false);
  const copyFeed = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  return (
    <div data-testid="follow-page">
      <Seo
        title="Follow & Listen"
        description={`Follow ${show.title} in your favorite podcast app — any podcast app.`}
        image={show.image}
        path="/follow"
      />

      <section className="hero-bg border-b border-[#0b1f4d]/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9a6700]">
            Follow &amp; Listen
          </span>
          <h1 className="font-poster uppercase text-5xl sm:text-6xl text-[#0b1f4d] mt-2 tracking-tight">
            Listen in your app
          </h1>
          <p className="mt-4 text-lg text-[#374566] max-w-2xl mx-auto">
            Add {show.title} to your favorite podcast app with the RSS feed below.
            New episodes arrive automatically.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {siteConfig.follow.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {siteConfig.follow.map((p) => (
            <a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`listen-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-link`}
              className="group flex flex-col items-center justify-center gap-3 rounded-md border border-[#0b1f4d]/10 bg-white px-4 py-6 transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-[0_10px_40px_-12px_rgba(245,182,28,0.4)] hover:border-[#f5b61c]/60"
            >
              <img
                src={p.icon}
                alt={p.name}
                className="h-12 w-12 rounded-lg object-contain"
                loading="lazy"
              />
              <span className="text-sm font-semibold text-[#1f2d54] group-hover:text-[#9a6700] transition-colors text-center">
                {p.name}
              </span>
            </a>
          ))}
        </div>
        )}

        {/* RSS */}
        <div className="rounded-lg border border-[#0b1f4d]/10 bg-white p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <Rss size={20} className="text-[#9a6700]" />
            <h2 className="font-display uppercase text-xl font-semibold text-[#0b1f4d]">
              Prefer to add it manually?
            </h2>
          </div>
          <p className="mt-2 text-sm text-[#4d5a78]">
            Copy the RSS feed URL and paste it into any podcast app.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <code className="flex-1 rounded-sm bg-[#f3ecdc] px-4 py-3 text-xs sm:text-sm font-mono text-[#8a5a00] break-all">
              {feedUrl}
            </code>
            <button
              data-testid="copy-rss-button"
              onClick={copyFeed}
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] font-bold uppercase tracking-wide px-5 py-3 transition-colors shrink-0"
            >
              {copied ? (
                <>
                  <Check size={17} /> Copied
                </>
              ) : (
                <>
                  <Copy size={17} /> Copy feed
                </>
              )}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
